import { motion } from "framer-motion";
import { CheckCircle2, Zap, Shield, Layers, Target, Star } from "lucide-react";
import { useState, useEffect } from "react";
import { ContactModal } from "@/components/ContactModal";
import { useScrollAnimations } from "@/components/GSAPScrollAnimations";

const LogoDesignToolsComparison = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainer = useScrollAnimations();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Logo Design Tools Comparison",
      "description": "Compare professional logo design tools vs DIY options. Learn which software is best for your needs and budget.",
      "author": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "url": "https://koech-creatives.onrender.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Koech Creatives",
        "logo": {
          "@type": "ImageObject",
          "url": "https://koech-creatives.onrender.com/og-image.png"
        }
      },
      "datePublished": "2026-07-13",
      "dateModified": "2026-07-13",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://koech-creatives.onrender.com/logo-design-tools-comparison"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main ref={scrollContainer} className="min-h-screen bg-gradient-to-b from-background to-accent-navy/30">
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Logo Design Tools Comparison
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            className="text-xl text-muted-foreground mb-8"
            >
            Compare the best logo design software and tools available. Find the right tool for your skill level, budget, and design needs to create professional logos.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Professional Logo Design
          </motion.button>
        </div>
      </section>

      {/* Professional Tools */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Professional Design Software
          </motion.h2>
          <div className="space-y-6">
            {[
              {
                name: "Adobe Illustrator",
                price: "$20.99/month",
                pros: ["Industry standard", "Vector-based", "Advanced features", "Cloud integration"],
                cons: ["Steep learning curve", "Subscription required", "Resource intensive"],
                bestFor: "Professional designers and agencies"
              },
              {
                name: "CorelDRAW",
                price: "$22.42/month",
                pros: ["Powerful vector tools", "Windows-native", "Extensive template library", "One-time purchase option"],
                cons: ["Mac version limited", "Less popular than Illustrator", "Steeper learning curve"],
                bestFor: "Windows-based professional designers"
              },
              {
                name: "Affinity Designer",
                price: "$69.99 one-time",
                pros: ["One-time purchase", "Professional features", "Cross-platform", "Affordable"],
                cons: ["Smaller community", "Fewer tutorials", "Limited cloud features"],
                bestFor: "Budget-conscious professionals"
              },
            ].map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold text-xl">{tool.name}</h3>
                  <span className="text-coral font-bold">{tool.price}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-semibold text-green-400 mb-2">Pros:</h4>
                    <ul className="space-y-1">
                      {tool.pros.map((pro, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-3 h-3 text-green-400" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-400 mb-2">Cons:</h4>
                    <ul className="space-y-1">
                      {tool.cons.map((con, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Zap className="w-3 h-3 text-red-400" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Best For: {tool.bestFor}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Web-Based Tools */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Web-Based Design Tools
          </motion.h2>
          <div className="space-y-6">
            {[
              {
                name: "Figma",
                price: "Free / $12/month",
                pros: ["Free tier available", "Collaborative features", "Browser-based", "Growing community"],
                cons: ["Requires internet", "Limited offline features", "Newer than established tools"],
                bestFor: "Teams and remote collaboration"
              },
              {
                name: "Canva",
                price: "Free / $12.99/month",
                pros: ["Very beginner-friendly", "Templates included", "Web interface", "Affordable"],
                cons: ["Limited vector control", "Template limitations", "Not for professional output"],
                bestFor: "Beginners and non-designers"
              },
              {
                name: "Sketch",
                price: "$9/month",
                pros: ["Mac-native", "Intuitive interface", "Plugin ecosystem", "Popular with UI designers"],
                cons: ["Mac only", "No Windows support", "Subscription required"],
                bestFor: "Mac-based UI/UX designers"
              },
            ].map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold text-xl">{tool.name}</h3>
                  <span className="text-coral font-bold">{tool.price}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-semibold text-green-400 mb-2">Pros:</h4>
                    <ul className="space-y-1">
                      {tool.pros.map((pro, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-3 h-3 text-green-400" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-400 mb-2">Cons:</h4>
                    <ul className="space-y-1">
                      {tool.cons.map((con, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Zap className="w-3 h-3 text-red-400" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Best For: {tool.bestFor}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tools */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Free Logo Design Tools
          </motion.h2>
          <div className="space-y-6">
            {[
              {
                name: "Inkscape",
                price: "Free",
                pros: ["Completely free", "Open source", "Vector-based", "Cross-platform"],
                cons: ["Steep learning curve", "Limited support", "Less intuitive interface"],
                bestFor: "Budget-conscious designers willing to learn"
              },
              {
                name: "GIMP",
                price: "Free",
                pros: ["Free and open source", "Powerful features", "Large community", "Cross-platform"],
                cons: ["Not vector-based", "Raster only", "Complex interface"],
                bestFor: "Raster design and photo editing"
              },
              {
                name: "Hatchful",
                price: "Free",
                pros: ["AI-powered suggestions", "Easy to use", "Quick results", "No design skills needed"],
                cons: ["Limited customization", "Template-based", "Generic designs"],
                bestFor: "Quick prototypes and non-designers"
              },
            ].map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-6 rounded-lg border border-gray-800"
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-semibold text-xl">{tool.name}</h3>
                  <span className="text-coral font-bold">{tool.price}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-semibold text-green-400 mb-2">Pros:</h4>
                    <ul className="space-y-1">
                      {tool.pros.map((pro, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-3 h-3 text-green-400" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-red-400 mb-2">Cons:</h4>
                    <ul className="space-y-1">
                      {tool.cons.map((con, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Zap className="w-3 h-3 text-red-400" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Best For: {tool.bestFor}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Choosing the Right Tool */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            How to Choose the Right Tool
          </motion.h2>
          <div className="space-y-4">
            {[
              { factor: "Skill Level", desc: "Beginners: Canva, Hatchful | Intermediate: Figma, Sketch | Advanced: Illustrator, CorelDRAW" },
              { factor: "Budget", desc: "Free: Inkscape, GIMP, Hatchful | Low: Affinity Designer | High: Adobe Creative Cloud" },
              { factor: "Platform", desc: "Mac: Sketch, Illustrator | Windows: CorelDRAW, Illustrator | Web: Figma, Canva" },
              { factor: "Output Requirements", desc: "Vector: Illustrator, Inkscape | Print: CorelDRAW, Illustrator | Web: Figma, Sketch" },
            ].map((item, index) => (
              <motion.div
                key={item.factor}
                initial={{ x: -20, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                className="bg-card p-4 rounded-lg border border-gray-800 flex gap-4"
              >
                <Target className="w-8 h-8 text-coral flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">{item.factor}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional vs DIY */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Tool vs Designer: What Matters Most
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-coral">The Tool Matters</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><Star className="w-4 h-4 text-coral" /> Vector quality for scalability</li>
                <li className="flex items-center gap-2"><Star className="w-4 h-4 text-coral" /> File format compatibility</li>
                <li className="flex items-center gap-2"><Star className="w-4 h-4 text-coral" /> Feature set for your needs</li>
                <li className="flex items-center gap-2"><Star className="w-4 h-4 text-coral" /> Learning curve and support</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              
              className="bg-card p-6 rounded-lg border border-gray-800"
            >
              <h3 className="font-semibold text-xl mb-4 text-muted-foreground">The Designer Matters More</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" /> Strategic thinking and planning</li>
                <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" /> Design principles expertise</li>
                <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" /> Brand understanding and research</li>
                <li className="flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" /> Experience and portfolio quality</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="py-16 px-4 scroll-section">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-8"
          >
            Related Resources
          </motion.h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Ultimate Guide to Logo Design Services", url: "/logo-design-services-guide", desc: "Comprehensive guide to professional logo design" },
              { title: "How to Design a Logo", url: "/how-to-design-a-logo", desc: "Step-by-step logo design process" },
              { title: "What Makes a Good Logo", url: "/what-makes-a-good-logo", desc: "Learn the essential qualities of effective logos" },
              { title: "Logo Design Principles", url: "/logo-design-principles", desc: "Master the fundamental design principles" },
            ].map((resource, index) => (
              <motion.a
                key={resource.title}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                
                href={resource.url}
                className="bg-card p-6 rounded-lg border border-gray-800 hover:border-coral transition-colors block"
              >
                <h3 className="font-semibold text-lg mb-2">{resource.title}</h3>
                <p className="text-muted-foreground">{resource.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-accent-navy/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-4"
          >
            Skip the Tools, Get Professional Results
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            className="text-xl text-muted-foreground mb-8"
            >
            While tools are important, expert designers with strategic thinking create logos that truly build brand recognition and drive business success.
          </motion.p>
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="bg-coral text-white px-8 py-3 rounded-full font-medium text-lg shadow-lg hover:bg-coral/90 transition-colors"
          >
            Get Professional Logo Design
          </motion.button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </main>
  );
};

export default LogoDesignToolsComparison;
